import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { ComboboxInput } from "@/components/ui/ComboboxInput";
import { MoneyInput } from "@/components/ui/MoneyInput";
import { CloseIcon } from "@/components/icons";
import { formatMoney } from "@/utils/currency";
import { cn } from "@/utils/cn";
import type { WorkValues } from "../types";
import { pctText } from "../utils/format";
import { workTotal } from "../utils/workValues";

export interface WorkTypeOption {
  id: string;
  label: string;
}

export { pctText };
const pctOf = (part: number, whole: number) => (whole ? (part / whole) * 100 : 0);

/** A percent field that keeps what you type while focused, like MoneyInput. */
export function PercentInput({ value, onChange, ariaLabel, disabled, max = 100 }: {
  value: number;
  onChange: (v: number) => void;
  ariaLabel: string;
  disabled?: boolean;
  /** The highest percent it takes: 100 for a share, more for a markup. */
  max?: number;
}) {
  const [text, setText] = useState(pctText(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(pctText(value));
  }, [value, focused]);
  return (
    <div className="relative w-24 shrink-0">
      <input
        type="text"
        inputMode="decimal"
        aria-label={ariaLabel}
        value={text}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          setText(e.target.value);
          const n = Number(e.target.value.replace(",", "."));
          if (!Number.isNaN(n)) onChange(Math.max(0, Math.min(max, n)));
        }}
        className="w-full h-9 pl-3 pr-7 rounded-lg border border-gray-200 bg-white text-sm text-right tabular-nums disabled:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
      />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
    </div>
  );
}

interface PriceSplitEditorProps {
  priceMinor: number;
  values: WorkValues;
  onChange: (values: WorkValues) => void;
  /** The work type catalog (DB-owned, TD-075). */
  workTypes: WorkTypeOption[];
  /** Adds a work type to the catalog and returns its id. Without it, only existing work types can be added. */
  onCreateWorkType?: (label: string) => Promise<string>;
  /** The agency's target share in percent, shown beside what it keeps. */
  targetPct?: number;
  /** Values of the version this one replaces: shown as "was" and flagged when a value drops below (TD-078). */
  previous?: WorkValues;
  /** Prefilled values: a row that differs is flagged as edited. */
  prefill?: WorkValues;
  /** One line under the title, e.g. how the prefill was worked out. */
  hint?: ReactNode;
  /** The agency's own client (TD-105, R30): the last row is "The agency pays" the people's total, with no share, never red. */
  own?: boolean;
  /** Header control, e.g. Reset to prefill. */
  action?: ReactNode;
  /** Extra control per row, e.g. who does a one-off's work. */
  rowExtra?: (workType: string) => ReactNode;
  /** ISO currency of the price. */
  currency?: string;
  /** Show the split as text, e.g. a published price book that no longer changes. */
  readOnly?: boolean;
  /** Percents only, no amounts: `priceMinor` is the whole (100 for whole percents) and each value its part, e.g. a service line's split defaults. */
  percentOnly?: boolean;
}

/**
 * Splits a price between work types and the agency. Each work type takes a percent or an amount and
 * the other follows. The agency keeps the remainder, so the split always adds up to the price.
 */
export function PriceSplitEditor({ priceMinor, values, onChange, workTypes, onCreateWorkType, targetPct, previous, prefill, hint, own = false, action, rowExtra, readOnly, percentOnly, currency = "EUR" }: PriceSplitEditorProps) {
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  // The latest values, so a split edit made while a work type is being created is not overwritten.
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const agency = priceMinor - workTotal(values);
  const agencyPct = pctOf(agency, priceMinor);
  const deficit = !own && agency < 0;
  const label = (id: string) => workTypes.find((w) => w.id === id)?.label ?? id;
  const set = (id: string, v: number) => onChange({ ...values, [id]: Math.max(0, v) });
  const free = workTypes.filter((w) => !(w.id in values));
  const match = workTypes.find((w) => w.label.toLowerCase() === query.trim().toLowerCase());
  const canCreate = !!onCreateWorkType && !!query.trim() && !match;
  const add = async () => {
    const label = query.trim();
    if (creating || !label || (!match && !onCreateWorkType)) return;
    let id = match?.id;
    if (!id && onCreateWorkType) {
      setCreating(true);
      try {
        id = await onCreateWorkType(label);
      } catch {
        return; // the create mutation already toasted why
      } finally {
        setCreating(false);
      }
    }
    if (id && !(id in valuesRef.current)) onChange({ ...valuesRef.current, [id]: 0 });
    setQuery("");
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void add();
    }
  };
  // No price typed yet, no amber: there is nothing to be below a target of (S-F5).
  const belowTarget = targetPct !== undefined && priceMinor > 0 && agencyPct < targetPct - 0.005;
  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-1 min-h-8">
        <span className="text-xs font-medium text-gray-600">How the price splits</span>
        {action}
      </div>
      {hint && <p className="text-xs text-gray-500 mb-2">{hint}</p>}
      <div className="rounded-lg bg-gray-50 divide-y divide-gray-100">
        {Object.keys(values).map((id) => {
          const below = previous !== undefined && values[id] < (previous[id] ?? 0);
          const edited = prefill !== undefined && values[id] !== (prefill[id] ?? 0);
          return (
            // On a phone the name takes its own line and the numbers go under it, so it stays readable at 390px.
            <div key={id} className="flex flex-wrap sm:flex-nowrap items-center justify-end gap-2 px-3 py-2">
              <div className="w-full sm:w-auto sm:flex-1 min-w-0">
                <div className="flex items-center gap-2 text-sm text-gray-900">
                  <span className="truncate">{label(id)}</span>
                  {below ? <Badge tone="red">below previous</Badge> : edited ? <Badge tone="amber">edited</Badge> : null}
                </div>
                {previous && <div className="text-xs text-gray-500 tabular-nums">was {formatMoney(previous[id] ?? 0, currency)}</div>}
              </div>
              {rowExtra?.(id)}
              {readOnly ? (
                <>
                  <span className="w-24 shrink-0 pr-3 text-right text-sm tabular-nums text-gray-500">{pctText(pctOf(values[id], priceMinor))}%</span>
                  <span className="w-32 shrink-0 pr-3 text-right text-sm tabular-nums">{formatMoney(values[id], currency)}</span>
                </>
              ) : (
                <>
                  <PercentInput value={pctOf(values[id], priceMinor)} disabled={!priceMinor} ariaLabel={`${label(id)} percent`} onChange={(p) => set(id, Math.round((priceMinor * p) / 100))} />
                  {!percentOnly && (
                    <MoneyInput className="w-32 shrink-0" valueMinor={values[id]} currency={currency} invalid={below} ariaLabel={`${label(id)} amount`} onChange={(v) => set(id, v ?? 0)} />
                  )}
                  <button type="button" onClick={() => onChange(Object.fromEntries(Object.entries(values).filter(([k]) => k !== id)))} aria-label={`Remove ${label(id)}`} className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                    <CloseIcon className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          );
        })}
        {!readOnly && (free.length > 0 || onCreateWorkType) && (
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="flex-1">
              <ComboboxInput value={query} onChange={setQuery} options={free.map((w) => w.label)} placeholder={onCreateWorkType ? "Add a work type, or type a new one" : "Add a work type"} className="w-full h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm" onKeyDown={onKey} />
            </div>
            <Button size="sm" variant="secondary" className="whitespace-nowrap" onClick={() => void add()} disabled={creating || (!match && !canCreate)}>
              {canCreate ? `Create "${query.trim()}"` : "Add"}
            </Button>
          </div>
        )}
        {/* A balance, red below zero (DD-054). The own client's row is what its people take, a flow with no share (TD-105). */}
        <div className={cn("flex flex-wrap sm:flex-nowrap items-center justify-end gap-2 px-3 py-2.5", deficit && "bg-red-50")}>
          <div className="w-full sm:w-auto sm:flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-900">{own ? "The agency pays" : "Agency keeps"}</div>
            {!own && (
              <div className={cn("text-xs", deficit ? "text-red-600" : belowTarget ? "text-amber-700" : "text-gray-500")}>
                {deficit ? "The work costs more than the price" : `what the work leaves${targetPct !== undefined ? `, target ${pctText(targetPct)}%` : ""}`}
              </div>
            )}
          </div>
          <span className={cn("w-24 shrink-0 pr-3 text-right text-sm tabular-nums", deficit ? "text-red-600" : "text-gray-700")}>{own ? "" : `${pctText(agencyPct)}%`}</span>
          {!percentOnly && <span className={cn("w-32 shrink-0 pr-3 text-right text-sm font-semibold tabular-nums", deficit && "text-red-600")}>{formatMoney(own ? workTotal(values) : agency, currency)}</span>}
          {!readOnly && <span className="w-7 shrink-0" />}
        </div>
      </div>
    </div>
  );
}
